import { FC, useEffect, useState } from 'react'
import CN from 'classnames'
import {
  useGetProcessOptions,
  useGetTagOptions,
  useUpdateEmailAlertGroup,
} from 'framework/api/methods'

import { Button } from 'components/atoms/Button'
import { Drawer } from 'components/atoms/Drawer'
import { Input } from 'components/atoms/Input'

import { NotificationRecipientCard } from '../../pages/configurations/EmailNotificationRecipients/EmailAlertsCardList/EmailAlertReportCard'

export interface UpdateEmailAlertGroupProps {
  className?: string | undefined
  isActive: boolean
  card: NotificationRecipientCard | null
  onCloseClick: () => void
  notify: (message: string, type: 'success' | 'error') => void
  onUpdated: () => void
}

export const UpdateEmailAlertGroup: FC<UpdateEmailAlertGroupProps> = ({
  className,
  isActive,
  card,
  onCloseClick,
  notify,
  onUpdated,
}: UpdateEmailAlertGroupProps) => {
  const { data: processOptionsData } = useGetProcessOptions()
  const { data: tagOptionsData } = useGetTagOptions()
  const processOptions: string[] = processOptionsData?.processOptions ?? []
  const tagOptions: string[] = tagOptionsData?.tagOptions ?? []

  const [serviceName, setServiceName] = useState('')
  const [serviceNameDescription, setServiceNameDescription] = useState('')
  const [tag, setTag] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (card) {
      setServiceName(card.serviceName)
      setServiceNameDescription(card.serviceNameDescription)
      setTag(card.tag)
      setErrors({})
    }
  }, [card])

  const { mutate: updateGroup, isLoading: isUpdating } =
    useUpdateEmailAlertGroup()

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!serviceName.trim()) nextErrors.serviceName = 'Service name is required'
    if (!serviceNameDescription.trim())
      nextErrors.serviceNameDescription = 'Process/description is required'
    if (!tag.trim()) nextErrors.tag = 'Tag is required'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = () => {
    if (!card || !validate()) return

    updateGroup(
      {
        partnerId: card.partnerId,
        currentServiceName: card.serviceName,
        serviceName: serviceName.trim(),
        serviceNameDescription: serviceNameDescription.trim(),
        tag: tag.trim(),
      },
      {
        onSuccess: () => {
          notify(`Updated ${serviceName.trim()}`, 'success')
          onCloseClick()
          onUpdated()
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData?.message || 'Could not update email group', 'error')
        },
      },
    )
  }

  return (
    <Drawer
      isActive={isActive}
      onCloseClick={onCloseClick}
      width={440}
      title='Edit Email Group'>
      <div
        className={CN(
          'flex flex-col py-4 px-5 relative h-full overflow-y-auto overscroll-y-auto styled-scroll gap-y-5',
          className,
        )}>
        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>Partner</span>
          <span className='text-sm text-N-800 font-semibold py-[9px] px-4 bg-N-50 border border-N-100 rounded-md'>
            {card?.partnerName}{' '}
            {card?.partnerCode ? `(${card.partnerCode})` : ''}
          </span>
        </div>

        <div className='flex flex-col w-full'>
          <Input
            labelValue='Service Name'
            isRequired
            type='text'
            value={serviceName}
            onChange={(e: any) => setServiceName(e.target.value)}
            isError={!!errors.serviceName}
          />
          {errors.serviceName && (
            <span className='text-R-500 text-xs pt-1'>
              {errors.serviceName}
            </span>
          )}
        </div>

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Process / Service Description
            <span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-group-process-options'
            value={serviceNameDescription}
            onChange={(e) => setServiceNameDescription(e.target.value)}
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.serviceNameDescription },
            )}
          />
          <datalist id='email-alert-group-process-options'>
            {processOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.serviceNameDescription && (
            <span className='text-R-500 text-xs pt-1'>
              {errors.serviceNameDescription}
            </span>
          )}
        </div>

        <div className='flex flex-col w-full'>
          <span className='pb-2 text-N-700'>
            Tag<span className='text-R-500 ml-[2px]'>*</span>
          </span>
          <input
            list='email-alert-group-tag-options'
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className={CN(
              'w-full outline-none border border-N-200 rounded-md text-sm py-[9px] px-4',
              { 'border-R-200': !!errors.tag },
            )}
          />
          <datalist id='email-alert-group-tag-options'>
            {tagOptions.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
          {errors.tag && (
            <span className='text-R-500 text-xs pt-1'>{errors.tag}</span>
          )}
        </div>

        <span className='text-xs text-N-400'>
          Changes apply to every recipient in this group. Manage individual
          recipient emails via &quot;Manage recipients&quot;.
        </span>
      </div>

      <div className='drawer-footer flex items-center w-full h-[67px] sticky bottom-0 px-5 border-t border-N-200 shadow-sm gap-x-5'>
        <Button
          isLoading={isUpdating}
          disabled={isUpdating}
          isFullWidth
          onClick={handleSave}>
          Save
        </Button>

        <Button isFullWidth appearance='dull' onClick={onCloseClick}>
          Close
        </Button>
      </div>
    </Drawer>
  )
}

UpdateEmailAlertGroup.defaultProps = {
  className: undefined,
}

export default UpdateEmailAlertGroup
